import { supabase } from './supabaseClient';
import { getCurrentSeasonId, getSeasonWindow } from '../constants/pointValues';

/**
 * Fetch the current user's points breakdown for the active season.
 * Groups point_transactions by `reason` within the current season window.
 *
 * @returns {Promise<{ breakdown: Record<string, number>, total: number } | null>}
 */
export const fetchSeasonBreakdown = async () => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const seasonId = getCurrentSeasonId();
  const { start, end } = getSeasonWindow(seasonId);

  // Add 1 day to end to make it inclusive of the last day
  const endInclusive = new Date(end.getTime() + 86_400_000);

  const { data, error } = await supabase
    .from('point_transactions')
    .select('points, reason')
    .eq('user_id', user.id)
    .gte('created_at', start.toISOString())
    .lt('created_at', endInclusive.toISOString())
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[pointsApi] fetchSeasonBreakdown failed:', error);
    return null;
  }

  // Group by reason
  const breakdown = {};
  let total = 0;
  for (const row of data || []) {
    breakdown[row.reason] = (breakdown[row.reason] || 0) + row.points;
    total += row.points;
  }

  return { breakdown, total };
};

/**
 * Fetch the user's previous season rank (used for season recap).
 * Queries player_points for everyone in the previous season, ranked.
 *
 * @param {number} previousSeasonId
 * @param {number} previousSeasonPoints
 * @returns {Promise<number|null>} rank, or null
 */
export const fetchPreviousSeasonRank = async (previousSeasonId, previousSeasonPoints) => {
  // Approximate rank: count how many players had more points in that season.
  // Since we lazy-reset, players with season_id = previousSeasonId still have their season_points.
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;

  const { count, error } = await supabase
    .from('player_points')
    .select('*', { count: 'exact', head: true })
    .eq('season_id', previousSeasonId)
    .gt('season_points', previousSeasonPoints);

  if (error) {
    console.error('[pointsApi] fetchPreviousSeasonRank failed:', error);
    return null;
  }

  // Rank = number of people above you + 1
  return (count ?? 0) + 1;
};
